import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Ban,
  BookOpen,
  FileText,
  GraduationCap,
  Inbox,
  Library,
  Mail,
  Megaphone,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  Trophy,
  UserPlus,
  Users,
  UserX,
  Activity,
} from "lucide-react";
import api from "../../../services/api";
import { ChartDataTable, GroupedBarChart, LineChart } from "./ActivityChart";
import { EmptyState, Loading, errorText, formatScore, timeAgo, formatDateTime } from "./common";

const StatCard = ({ icon: Icon, label, value, hint, onClick, tone }) => {
  const content = (
    <>
      <div className="stat-icon">
        <Icon size={22} />
      </div>
      <div>
        <div className="admin-stat-value">{value ?? "—"}</div>
        <div className="admin-stat-label">{label}</div>
        {hint && <div className="admin-stat-hint">{hint}</div>}
      </div>
    </>
  );
  const cls = `stat-card admin-stat ${tone ? `admin-stat-${tone}` : ""}`;
  return onClick ? (
    <button type="button" className={`${cls} admin-stat-link`} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div className={cls}>{content}</div>
  );
};

const EVENT_META = {
  signup: { icon: UserPlus, label: "Sign-up" },
  quiz: { icon: Trophy, label: "Quiz" },
  tutor: { icon: MessageSquare, label: "Tutor" },
  contact: { icon: Mail, label: "Contact" },
};

const ActivityCard = () => {
  const [days, setDays] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState("bars");

  useEffect(() => {
    api
      .get("/admin/activity/timeseries", { params: { days: 14 } })
      .then((res) => setDays(Array.isArray(res.data?.days) ? res.data.days : []))
      .catch((err) => setError(errorText(err, "Couldn't load activity for the last 14 days.")));
  }, []);

  const empty =
    days && days.every((d) => !Number(d.signups) && !Number(d.quizzes) && !Number(d.tutor));

  return (
    <section className="dashboard-section admin-chart-card" aria-labelledby="admin-chart-heading">
      <div className="admin-section-head">
        <h3 id="admin-chart-heading">Last 14 days</h3>
        <div className="admin-segmented" role="group" aria-label="Chart view">
          <button
            type="button"
            className={view === "bars" ? "active" : ""}
            aria-pressed={view === "bars"}
            onClick={() => setView("bars")}
          >
            Sign-ups &amp; quizzes
          </button>
          <button
            type="button"
            className={view === "tutor" ? "active" : ""}
            aria-pressed={view === "tutor"}
            onClick={() => setView("tutor")}
          >
            Tutor messages
          </button>
        </div>
      </div>

      {error ? (
        <div className="admin-error">{error}</div>
      ) : !days ? (
        <Loading label="Loading activity…" />
      ) : days.length === 0 ? (
        <EmptyState icon={Activity} title="No activity data yet" />
      ) : (
        <>
          <div className="admin-chart-scroll">
            {view === "bars" ? (
              <GroupedBarChart
                days={days}
                title="Daily sign-ups and completed quizzes, last 14 days"
                series={[
                  { key: "signups", label: "Sign-ups", className: "series-a" },
                  { key: "quizzes", label: "Quizzes", className: "series-b" },
                ]}
              />
            ) : (
              <LineChart
                days={days}
                valueKey="tutor"
                label="Tutor messages"
                title="Daily tutor messages, last 14 days"
              />
            )}
          </div>
          <ChartDataTable
            days={days}
            caption="Daily activity, last 14 days"
            columns={[
              { key: "signups", label: "Sign-ups" },
              { key: "quizzes", label: "Quizzes completed" },
              { key: "tutor", label: "Tutor messages" },
            ]}
          />
          <div className="admin-legend" aria-hidden="true">
            {view === "bars" ? (
              <>
                <span><i className="series-a" /> Sign-ups</span>
                <span><i className="series-b" /> Quizzes completed</span>
              </>
            ) : (
              <span><i className="series-b" /> Tutor messages</span>
            )}
          </div>
          {empty && <p className="admin-hint">No activity recorded in the last 14 days.</p>}
        </>
      )}
    </section>
  );
};

const RecentActivity = () => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/activity/recent", { params: { limit: 15 } });
      setEvents(Array.isArray(res.data?.events) ? res.data.events : []);
    } catch (err) {
      setError(errorText(err, "Couldn't load recent activity."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="dashboard-section admin-feed-card" aria-labelledby="admin-feed-heading">
      <div className="admin-section-head">
        <h3 id="admin-feed-heading">Recent activity</h3>
        <button
          type="button"
          className="admin-icon-btn"
          onClick={load}
          disabled={loading}
          aria-label="Refresh recent activity"
          title="Refresh"
        >
          <RefreshCw size={16} className={loading ? "admin-spin" : ""} />
        </button>
      </div>
      {error ? (
        <div className="admin-error">{error}</div>
      ) : !events ? (
        <Loading label="Loading recent activity…" />
      ) : events.length === 0 ? (
        <EmptyState icon={Activity} title="Nothing yet">
          Sign-ups, quizzes, tutor chats and contact messages will show up here.
        </EmptyState>
      ) : (
        <ul className="admin-feed">
          {events.map((ev, i) => {
            const meta = EVENT_META[ev.type] || { icon: Activity, label: ev.type };
            const Icon = meta.icon;
            return (
              <li key={`${ev.type}-${ev.at}-${i}`}>
                <span className={`admin-feed-icon admin-feed-${ev.type}`} title={meta.label}>
                  <Icon size={16} />
                </span>
                <div className="admin-feed-body">
                  <strong>{ev.title}</strong>
                  {ev.detail && <small>{ev.detail}</small>}
                </div>
                {ev.at && (
                  <time dateTime={ev.at} title={formatDateTime(ev.at)}>
                    {timeAgo(ev.at)}
                  </time>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

const OverviewPanel = ({ stats, statsError, onRetryStats, onQuickAction }) => {
  return (
    <>
      <section className="dashboard-section admin-quick" aria-label="Quick actions">
        <span className="admin-quick-label">Quick actions</span>
        <div className="admin-quick-actions">
          <button type="button" className="eg-btn eg-btn-accent" onClick={() => onQuickAction("add-user")}>
            <UserPlus size={18} /> Add user
          </button>
          <button type="button" className="eg-btn eg-btn-outline" onClick={() => onQuickAction("new-announcement")}>
            <Megaphone size={18} /> New announcement
          </button>
          <button type="button" className="eg-btn eg-btn-outline" onClick={() => onQuickAction("unhandled-messages")}>
            <Inbox size={18} /> View unhandled messages
            {stats?.inbox?.unhandled_messages > 0 && (
              <span className="admin-count">{stats.inbox.unhandled_messages}</span>
            )}
          </button>
        </div>
      </section>

      {statsError ? (
        <div className="admin-error admin-error-row">
          <span>{statsError}</span>
          <button type="button" className="eg-btn eg-btn-outline" onClick={onRetryStats}>
            Try again
          </button>
        </div>
      ) : !stats ? (
        <Loading label="Loading platform numbers…" />
      ) : (
        <>
          <h3 className="admin-group-title">People</h3>
          <div className="admin-stats-grid">
            <StatCard icon={Users} label="Total users" value={stats.users?.total} hint={`${stats.users?.new_this_week ?? 0} new this week`} />
            <StatCard icon={GraduationCap} label="Students" value={stats.users?.students} />
            <StatCard icon={BookOpen} label="Teachers" value={stats.users?.teachers} />
            <StatCard icon={ShieldCheck} label="Admins" value={stats.users?.admins} />
          </div>

          <h3 className="admin-group-title">Needs attention</h3>
          <div className="admin-stats-grid">
            <StatCard icon={UserX} label="Unverified accounts" value={stats.users?.unverified} onClick={() => onQuickAction("unverified-users")} hint="View list" />
            <StatCard icon={Ban} label="Suspended accounts" value={stats.users?.suspended} onClick={() => onQuickAction("suspended-users")} hint="View list" />
            <StatCard icon={Inbox} label="Unhandled messages" value={stats.inbox?.unhandled_messages} onClick={() => onQuickAction("unhandled-messages")} hint={`${stats.inbox?.contact_messages ?? 0} messages in total`} />
          </div>

          <h3 className="admin-group-title">Learning</h3>
          <div className="admin-stats-grid">
            <StatCard icon={Trophy} label="Quizzes completed" value={stats.learning?.quizzes_completed} hint={`Average score ${formatScore(stats.learning?.average_score)}`} />
            <StatCard icon={MessageSquare} label="Tutor messages" value={stats.learning?.tutor_messages} />
            <StatCard icon={FileText} label="Summaries" value={stats.learning?.summaries} />
            <StatCard icon={Library} label="Curriculum documents" value={stats.learning?.curriculum_documents} />
          </div>

          {stats.learning?.curriculum_documents === 0 && (
            <div className="admin-notice">
              <AlertTriangle size={18} />
              <span>
                The curriculum collection is empty, so tutor answers and quizzes aren't grounded in the
                curriculum yet.
              </span>
            </div>
          )}
        </>
      )}

      <div className="admin-overview-grid">
        <ActivityCard />
        <RecentActivity />
      </div>
    </>
  );
};

export default OverviewPanel;
